"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { breadcrumbs } from "@/lib/logger/sentryBreadcrumb";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import { EVENTS } from "@/lib/analytics/trackEvent";
import { useLiveDetection } from "@/hooks/useLiveDetection";

import LiveDotOverlay from "./LiveDotOverlay";

interface LivePillCounterProps {
  medicationId?: string;
  medicationName?: string;
  onCountCaptured?: (count: number) => void;
}

export default function LivePillCounter({ medicationId, medicationName, onCountCaptured }: LivePillCounterProps) {
  const [capturedCount, setCapturedCount] = useState<number | null>(null);
  const [containerSize, setContainerSize] = useState({ w: 640, h: 480 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, controls] = useLiveDetection();
  const { start, stop, videoRef, processingCanvasRef } = controls;
  const { track } = useTrackEvent();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          w: Math.round(entry.contentRect.width),
          h: Math.round(entry.contentRect.height),
        });
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleStart = useCallback(async () => {
    breadcrumbs.pillScanStarted(medicationName || medicationId || "live");
    await start();
  }, [medicationId, medicationName, start]);

  const handleCapture = useCallback(() => {
    const count = state.stableCount > 0 ? state.stableCount : state.pillCount;
    setCapturedCount(count);
    stop();
    void track(EVENTS.LIVE_COUNT_CAPTURED, {
      pillCount: count,
      stableCount: state.stableCount,
      totalTracked: state.pillCount,
      fps: state.fps,
      preset: state.preset,
      medicationId: medicationId ?? null,
      medicationName: medicationName ?? null,
    });
  }, [medicationId, medicationName, state.fps, state.pillCount, state.preset, state.stableCount, stop, track]);

  const handleReset = useCallback(() => {
    setCapturedCount(null);
    stop();
  }, [stop]);

  const wrapper = (children: React.ReactNode) => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem",
        padding: "1.5rem",
        borderRadius: "14px",
        background: "#0B0B0B",
        minHeight: "380px",
        width: "100%",
        textAlign: "center",
      }}
    >
      {children}
    </div>
  );

  if (state.isOpenCvLoading) {
    return wrapper(
      <>
        <div style={{ width: 32, height: 32, border: "2px solid #333", borderTopColor: "#fff", borderRadius: "999px" }} className="ed-spin" />
        <div>
          <p style={{ color: "#fff", fontSize: "0.9rem", fontWeight: 600, margin: 0 }}>Loading detection engine</p>
          <p style={{ color: "#555", fontSize: "0.78rem", marginTop: 6 }}>This may take a moment on slow connections</p>
        </div>
      </>,
    );
  }

  if (state.status === "opencv_failed") {
    return wrapper(
      <>
        <div style={{ fontSize: "1.8rem" }}>⚠️</div>
        <div>
          <p style={{ color: "#fff", fontSize: "0.9rem", fontWeight: 600, margin: 0 }}>Live detection unavailable</p>
          <p style={{ color: "#666", fontSize: "0.78rem", marginTop: 6, maxWidth: 300 }}>{state.errorMessage}</p>
        </div>
        <button onClick={() => onCountCaptured?.(-1)} style={{ color: "#888", fontSize: "0.75rem", textDecoration: "underline", background: "transparent", border: "none" }}>
          Switch to Photo mode
        </button>
      </>,
    );
  }

  if (state.status === "denied") {
    return wrapper(
      <>
        <div style={{ fontSize: "1.8rem" }}>🚫</div>
        <div>
          <p style={{ color: "#fff", fontSize: "0.9rem", fontWeight: 600, margin: 0 }}>Camera access denied</p>
          <p style={{ color: "#666", fontSize: "0.78rem", marginTop: 6, maxWidth: 300 }}>
            Please allow camera access in your browser settings, then try again.
          </p>
          <p style={{ color: "#444", fontSize: "0.72rem", marginTop: 6 }}>On iPhone: Settings → Safari → Camera → Allow</p>
        </div>
        <button onClick={handleReset} className="ed-btn-ghost" style={{ fontSize: "0.75rem" }}>
          Try Again
        </button>
      </>,
    );
  }

  if (state.status === "no_support" || state.status === "error") {
    return wrapper(
      <>
        <div style={{ fontSize: "1.8rem" }}>📵</div>
        <div>
          <p style={{ color: "#fff", fontSize: "0.9rem", fontWeight: 600, margin: 0 }}>{state.status === "no_support" ? "Camera not supported" : "Couldn't access your camera"}</p>
          <p style={{ color: "#666", fontSize: "0.78rem", marginTop: 6, maxWidth: 300 }}>
            {state.errorMessage ?? "Check that you've allowed camera permissions and try again."}
          </p>
        </div>
        <button onClick={() => onCountCaptured?.(-1)} style={{ color: "#888", fontSize: "0.75rem", textDecoration: "underline", background: "transparent", border: "none" }}>
          Use Photo mode instead
        </button>
      </>,
    );
  }

  if (capturedCount !== null) {
    return wrapper(
      <>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <p style={{ color: "#666", fontSize: "0.7rem", margin: 0, letterSpacing: "0.12em", textTransform: "uppercase" }}>Captured</p>
          <p style={{ color: "#fff", fontWeight: 800, fontSize: "5rem", lineHeight: 1, margin: 0 }}>{capturedCount}</p>
          <p style={{ color: "#555", fontSize: "0.75rem", margin: 0 }}>pills detected</p>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={handleReset} className="ed-btn-ghost">
            Count Again
          </button>
          <button
            onClick={() => onCountCaptured?.(capturedCount)}
            style={{ background: "#fff", color: "#000", border: "none", borderRadius: 999, padding: "8px 16px", fontWeight: 700 }}
          >
            Confirm Count
          </button>
        </div>
      </>,
    );
  }

  if (state.status === "idle" || state.status === "requesting") {
    return wrapper(
      <>
        <div style={{ fontSize: "2.2rem" }}>💊</div>
        <div>
          <p style={{ color: "#fff", fontWeight: 700, fontSize: "1rem", margin: 0 }}>Live Count</p>
          <p style={{ color: "#555", fontSize: "0.84rem", marginTop: 6 }}>Point your camera at pills for instant detection</p>
          {medicationName ? <p style={{ color: "#444", fontSize: "0.74rem", marginTop: 6 }}>{medicationName}</p> : null}
        </div>
        <button
          onClick={() => void handleStart()}
          disabled={!state.isOpenCvReady || state.status === "requesting"}
          style={{ background: "#fff", color: "#000", border: "none", borderRadius: 999, padding: "10px 20px", fontWeight: 700, opacity: !state.isOpenCvReady || state.status === "requesting" ? 0.4 : 1 }}
        >
          {state.status === "requesting" ? "Starting..." : "Start Camera"}
        </button>
      </>,
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", background: "#0B0B0B", borderRadius: 14, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px 6px" }}>
        <p style={{ color: "#555", fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", margin: 0 }}>Live Count</p>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {state.fps > 0 ? <span style={{ color: "#3a3a3a", fontSize: "0.72rem" }}>{state.fps} fps</span> : null}
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "999px",
              background:
                state.preset === "HIGH" ? "#16a34a" : state.preset === "MEDIUM" ? "#d97706" : "#dc2626",
            }}
          />
        </div>
      </div>

      <div ref={containerRef} style={{ position: "relative", width: "100%", aspectRatio: "4 / 3", background: "#000" }}>
        <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <div
          style={{
            position: "absolute",
            top: "15%",
            left: "15%",
            right: "15%",
            bottom: "15%",
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: 16,
            pointerEvents: "none",
          }}
        />
        <LiveDotOverlay pills={state.trackedPills} containerWidth={containerSize.w} containerHeight={containerSize.h} />
        <canvas ref={processingCanvasRef} className="ed-visually-hidden" />
        {state.pillCount === 0 ? (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <p style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.85rem" }}>Place pills in frame</p>
          </div>
        ) : null}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: state.pillCount > 99 ? "2.5rem" : "3.5rem", lineHeight: 1 }}>
            {state.stableCount > 0 ? state.stableCount : state.pillCount}
          </span>
          <span style={{ color: "#444", fontSize: "0.72rem", marginTop: 4 }}>
            {state.stableCount > 0 && state.stableCount !== state.pillCount
              ? `${state.pillCount} detected, ${state.stableCount} confirmed`
              : "pills"}
          </span>
        </div>

        <button
          onClick={handleCapture}
          disabled={state.pillCount === 0}
          aria-label="Capture pill count"
          style={{
            width: 64,
            height: 64,
            borderRadius: "999px",
            border: "none",
            background: "#fff",
            opacity: state.pillCount === 0 ? 0.25 : 1,
            cursor: state.pillCount === 0 ? "not-allowed" : "pointer",
            display: "grid",
            placeItems: "center",
          }}
        >
          <div style={{ width: 24, height: 24, borderRadius: "999px", background: "#000" }} />
        </button>

        <button
          onClick={stop}
          style={{ background: "transparent", color: "#444", border: "none", fontSize: "0.75rem", cursor: "pointer" }}
        >
          Stop
        </button>
      </div>
    </div>
  );
}
